import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, Shield } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useValidateSender } from '../hooks/useSenders';
import { Sender } from '../types/sender.types';

// Validation schema
const otpSchema = z.object({
  otp: z
    .string()
    .length(6, 'OTP must be exactly 6 digits')
    .regex(/^\d{6}$/, 'OTP must contain only numbers'),
});

type OtpFormData = z.infer<typeof otpSchema>;

interface SenderValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  sender?: Sender;
}

export const SenderValidationModal = ({
  isOpen,
  onClose,
  sender,
}: SenderValidationModalProps) => {
  const { t } = useTranslation();
  const [attempts, setAttempts] = useState(0);
  // const [canResend, setCanResend] = useState(true);
  // const [resendCountdown, setResendCountdown] = useState(0);

  const validateSenderMutation = useValidateSender();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: '',
    },
    mode: 'onChange',
  });

  // Handle OTP input with auto-focus and formatting
  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue('otp', value);
  };

  // Resend countdown timer
  // useEffect(() => {
  //   if (resendCountdown > 0) {
  //     const timer = setTimeout(
  //       () => setResendCountdown(resendCountdown - 1),
  //       1000
  //     );
  //     return () => clearTimeout(timer);
  //   } else {
  //     setCanResend(true);
  //   }
  // }, [resendCountdown]);

  // const handleResendOtp = () => {
  //   // TODO: Implement resend OTP API call
  //   setCanResend(false);
  //   setResendCountdown(30); // 30 seconds cooldown
  //   toast.success(
  //     t('senders.validation.resendSuccess', 'OTP resent successfully')
  //   );
  // };

  const handleSubmitOtp = async (data: OtpFormData) => {
    if (!sender) return;

    try {
      await validateSenderMutation.mutateAsync({
        uuid: sender.uuid,
        otp: { otp: parseInt(data.otp) },
      });

      // Success - close modal and reset form
      reset();
      setAttempts(0);
      onClose();
    } catch {
      // Increment attempts on failure
      setAttempts(prev => prev + 1);

      // Clear form on failure
      setValue('otp', '');
    }
  };

  const handleClose = () => {
    reset();
    setAttempts(0);
    onClose();
  };

  const maxAttempts = 3;
  const isBlocked = attempts >= maxAttempts;

  console.log({ isBlocked, attempts, errors });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title={t('senders.validation.title', 'Verify Your Sender')}
    >
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <p className="mt-2 text-sm text-neutral-600">
            {t(
              'senders.validation.description',
              'Enter the 6-digit code sent to your email address'
            )}
          </p>
        </div>

        {/* Sender info */}
        {sender && (
          <div className="mb-6 rounded-lg bg-neutral-50 p-4">
            <div className="flex items-center gap-3">
              <div className="bg-primary-100 flex h-8 w-8 items-center justify-center rounded-lg">
                <Mail className="text-primary-600 h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-neutral-900">{sender.name}</p>
                <p className="text-sm text-neutral-600">{sender.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* OTP Form */}
        <form onSubmit={handleSubmit(handleSubmitOtp)} className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-900">
              {t('senders.validation.otpLabel', 'Verification Code')}
            </label>
            <input
              type="text"
              {...register('otp')}
              onChange={handleOtpChange}
              placeholder="000000"
              maxLength={6}
              className={`focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border px-4 py-3 text-center font-mono text-lg tracking-widest transition-colors focus:ring-2 ${
                errors.otp ? 'border-error-500' : 'border-neutral-300'
              }`}
              disabled={isBlocked}
              autoFocus
            />
            {errors.otp && (
              <p className="text-error-600 mt-1 text-sm">
                {errors.otp.message}
              </p>
            )}
          </div>

          {/* Attempts warning */}
          {attempts > 0 && (
            <div className="bg-warning-50 rounded-lg p-3">
              <p className="text-warning-800 text-sm">
                {t(
                  'senders.validation.attemptsWarning',
                  'Invalid code. Please try again.'
                )}
                {attempts >= maxAttempts && (
                  <span className="mt-1 block font-medium">
                    {t(
                      'senders.validation.maxAttempts',
                      'Maximum attempts reached. Please try again later.'
                    )}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Resend OTP */}
          {/* <div className="text-center">
            <p className="text-sm text-neutral-600">
              {t('senders.validation.didntReceive', "Didn't receive the code?")}
            </p>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={!canResend || isBlocked}
              className="text-primary-600 hover:text-primary-700 mt-2 inline-flex items-center gap-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" />
              {canResend
                ? t('senders.validation.resend', 'Resend Code')
                : t(
                    'senders.validation.resendCountdown',
                    'Resend in {{seconds}}s',
                    {
                      seconds: resendCountdown,
                    }
                  )}
            </button>
          </div> */}

          {/* Form actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={validateSenderMutation.isPending}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={
                Object.keys(errors).length > 0 ||
                isBlocked ||
                validateSenderMutation.isPending
              }
            >
              {validateSenderMutation.isPending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {t('senders.validation.verifying', 'Verifying...')}
                </>
              ) : (
                t('senders.validation.verify', 'Verify Sender')
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
